import React from 'react';
import { Grid, Tab, Typography } from '@material-ui/core';
import { isButtonDisabled } from 'src/sidebar/business-logic/button-disabled';
import { IButton } from 'src/common/interfaces/IButton';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { BaseButton } from 'src/common/components/base-button';
import { styles } from './styles';
import useScrollButtonState from '@ncr-retail/hooks/build/useScrollButtonState/useScrollButtonState';
import { Pagination } from 'src/pagination/pagination';
import { AppState } from 'src/common/redux/configure-store';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import { hasSufficientPrivileges } from 'src/sidebar/sidebar-operation';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
interface ActionWindowButtonsProps {
  actionButtons: IButton[];
  hasCustomer: boolean;
  disableButtons: boolean;
  hasCashDrawer: boolean;
  hasPinpad: boolean;
  hasProducts: boolean;
  selectedItem?: IElectronicJournalRecord;
  saleModeFromReturnMode: boolean;
  horizontalBtnAlignment: boolean;
  trainingModeActive?: boolean;
}

const ActionWindowButtons = ({
  actionButtons,
  hasCustomer,
  disableButtons,
  hasCashDrawer,
  hasPinpad,
  hasProducts,
  selectedItem,
  saleModeFromReturnMode,
  horizontalBtnAlignment,
  trainingModeActive = false,
}: ActionWindowButtonsProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const tabName = 'Action Window';
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();
  const isTransactionActive = useSelector(
    (state: AppState): boolean => !!state.transaction.transactionIdExists,
  );
  const isDisabled = isButtonDisabled({
    isTransactionActive,
    hasCustomer,
    disableButton: disableButtons,
    hasCashDrawer,
    hasPinpad,
    selectedItem,
    tabName,
    hasProducts,
    saleModeFromReturnMode,
  });

  const handleClick = async (button: IButton) => {
    if (button.command) {
      if (await hasSufficientPrivileges(button.command)) {
        return button.onClick();
      }
    }
  };

  return (
    <Grid
      container
      className='fullHeight'
      direction='column'
      alignContent='flex-start'
      data-testid='action-window-button-container'
    >
      <div
        ref={scrollRef}
        className={
          horizontalBtnAlignment
            ? `horizontalScroll fullWidth`
            : `verticalScroll ${classes.buttonsCustomContainer}`
        }
      >
        <Grid
          item
          container
          className={
            horizontalBtnAlignment
              ? `${classes.buttonContainerSmallForm}`
              : `${classes.buttonContainer}`
          }
        >
          {horizontalBtnAlignment ? (
            <CustomTabs
              value={false}
              className={`${classes.customTabs} ${
                trainingModeActive ? trainingModeClasses.disableBoxShadow : ''
              }`}
              spreadOut={true}
              newStyleArrows={true}
              classes={{
                flexContainer: classes.allTabsContainer,
                scrollButtons: classes.scrollButton,
              }}
            >
              {actionButtons?.map(button => {
                const buttonLabel =
                  typeof button.label === 'string' ? t(button.label) : button.label;
                const labelString = buttonLabel
                  .toString()
                  .replace(/\s/g, '-')
                  .toLowerCase();
                return (
                  <Tab
                    key={labelString}
                    label={buttonLabel}
                    className={classes.secondLevelTab}
                    centerRipple={true}
                    onClick={() => handleClick(button)}
                    data-testid={`action-window-button-${labelString}`}
                    disabled={isDisabled(button.appliesTo || []) || button.disabled}
                  />
                );
              })}
            </CustomTabs>
          ) : (
            actionButtons?.map(button => {
              const buttonLabel = typeof button.label === 'string' ? t(button.label) : button.label;
              const labelString = buttonLabel
                .toString()
                .replace(/\s/g, '-')
                .toLowerCase();

              return (
                <BaseButton
                  key={labelString}
                  buttonType='white'
                  onClick={() => handleClick(button)}
                  data-testid={`action-window-button-${labelString}`}
                  className={classes.actionButton}
                  disabled={isDisabled(button.appliesTo || []) || button.disabled}
                >
                  <Typography className={classes.labelText}>{buttonLabel}</Typography>
                </BaseButton>
              );
            })
          )}
        </Grid>
      </div>
      {!horizontalBtnAlignment && (
        <Grid container className={classes.autoTopMargin} justifyContent='space-evenly'>
          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
            updateCurrentPageHandler={onPageChanged}
            displayText={false}
            testIdPrefix={'action-window-button'}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ActionWindowButtons;
